import React, { useCallback, useState } from 'react';
import { DatePicker, Modal, Spin } from "antd";
import moment from "moment";

import useResource from 'hooks/useResource';
import useModal from "hooks/useModal";

import { ResourceType } from 'services/resources';
import useAdminResource from '../hooks/useAdminResource';

import AdminLayout from 'layouts/Admin.layout';
import FormModal from 'components/FormModal';
import Table from 'components/Table';
import Button from 'components/Button';
import Search from 'components/Search';

import DoorForm from '../components/forms/Door.form';
import { EditAction, DeleteAction } from '../components/TableActions';

import { mapLocationsToOptions } from 'helpers/mapToOptions';
import CameraView from "../../../components/process/CameraView";

const { RangePicker } = DatePicker;

const DoorsPage = () => {
  const locations = useResource(ResourceType.locations, { prefetch: true });
  const doors = useAdminResource({
    resource: ResourceType.doors,
    resourceName: 'door',
    searchFields: [
      'door_id',
      'name',
      'location_name',
    ],
  });

  const cameraModal = useModal();
  const [isCameraLoading, setIsCameraLoading] = useState(false);
  const [cameraData, setCameraData] = useState(null);
  const getDefaultDateRnage = () => [moment().subtract(5, "days"), moment()]
  const [dateRange, setDateRange] = useState(getDefaultDateRnage);

  const onAction = useCallback((actionType, item) => {
    switch (actionType) {
      case EditAction.actionType:
        doors.onEdit(item);
        break

      case DeleteAction.actionType:
        doors.onDelete(item.door_id);
        break;

      default: // make eslint happy :)
    }
  }, [doors]);

  const handleCameraClick = (record) => {
    cameraModal.show();
    setIsCameraLoading(true);
    setCameraData(null);

    fetchCameraData(record.door_id, dateRange[0].unix(), dateRange[1].unix());
  };

  const fetchCameraData = async (doorId, startTs, endTs) => {
    try {
      setIsCameraLoading(true);
      const response = await new Promise((resolve) =>
        setTimeout(() => {
          resolve({
            doorId,
            startTs,
            endTs,
            mode: "normal",
          });
        })
      );
      setCameraData(response);
    } catch (error) {
      console.log(error);
    } finally {
      setIsCameraLoading(false);
    }
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);

      if (cameraData?.doorId) {
        setIsCameraLoading(true);
        fetchCameraData(cameraData.doorId, dates[0].unix(), dates[1].unix());
      }
    }
  };

  return (
    <AdminLayout
      title="Doors"
      icon="airplay"
      extra={[
        <Search
          placeholder={doors.strings.searchPlaceholder}
          value={doors.searchVal}
          onChange={value => doors.search(value)}
        />,
        <Button
          type="primary"
          icon="plus"
          onClick={() => {
            doors.onCreate({ is_active: 1 });
          }}
        >
          {doors.strings.addButton}
        </Button>
      ]}
    >
      <FormModal
        title={doors.strings.formTitle}
        form={doors.form}
        submitText={doors.strings.formSubmitButton}
        onClose={() => doors.form.reset()}
      >
        <DoorForm form={doors.form} locationOptions={mapLocationsToOptions(locations.state.list)} />
      </FormModal>
      <Table
        loading={doors.isLoading || locations.state.isLoading}
        dataSource={doors.list}
        actions={[
          EditAction,
          DeleteAction,
        ]}
        actionsProps={{
          resource: 'door',
        }}
        onAction={onAction}
        rowKey="door_id"
      >
        <Table.Column title="ID" dataIndex="door_id" />
        <Table.Column title="Name" dataIndex="name" />
        <Table.Column title="Location" dataIndex="location_id" render={locationId => {
          const location = locations.state.list.find(l => l.location_id === locationId);
          if (!location) return '-';
          else return location.location_name;
        }} />
        <Table.Column title="Maintenance Mode" dataIndex="maintenance_mode" render={maintenanceMode => {
          return maintenanceMode ? 'Yes' : 'No';
        }} />
        <Table.Column title="Notes" dataIndex="notes" />
        <Table.Column title="Camera" key="camera" render={(text, record) => (
          <Button icon="video" onClick={() => handleCameraClick(record)} />
        )} />
      </Table>

      <Modal
        title="Camera View"
        visible={cameraModal.isVisible}
        onCancel={() => {
          cameraModal.hide();
          fetchCameraData(null);
        }}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: 16, textAlign: "center" }}>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="yyyy-MM-DD"
            disabledDate={(current) => {
              const [startDate] = dateRange;
              if (current && current > moment().endOf("day")) return true;

              if (startDate) {
                return current.isAfter(startDate.clone().add(5, "days"), "day");
              }

              return false;
            }}
          />
        </div>
        {isCameraLoading ? (
          <div style={{ textAlign: "center", padding: "50px 0" }}>
            <Spin size="large" />
            <p>Loading Camera Data...</p>
          </div>
        ) : cameraData ? (
          <CameraView
            doorId={cameraData.doorId}
            startTs={cameraData.startTs}
            endTs={cameraData.endTs}
            mode={cameraData.mode}
            autoplay={true}
          />
        ) : (
          <p>No camera data available.</p>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default DoorsPage;
